import type { Route } from '@/domains/appShell/layout/Layout'
import {
  getAccessibleChildren,
  getNavigationEntries,
  shouldHideNavigation,
} from '@/domains/appShell/layout/layout.utils'
import { useLogout } from '@/domains/appShell/services/useLogout'
import { userPermissionsAtom } from '@/domains/appShell/stores/user.store'
import { SideMenu } from '@/shared/ui/sideMenu'

import { type FC, type ReactNode } from 'react'
import {
  ArrowLeftRight,
  Bank,
  BoxArrowRight,
  CircleFill,
  ClipboardCheck,
  CollectionPlay,
  Database,
  FileEarmarkText,
  Folder2Open,
  FolderCheck,
  GraphUpArrow,
} from 'react-bootstrap-icons'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useAtomValue } from 'jotai'

/**
 * `Route.pathname` → icon shown in the SideMenu.
 *
 * The route config carries no icon (it only feeds the legacy menu), so one icon
 * is mapped per top-level entry here. Unmapped entries fall back to a generic
 * icon (`FALLBACK_ICON`).
 */
const ROUTE_ICONS: Record<string, ReactNode> = {
  onboardings: <ClipboardCheck />,
  'legal-consultation': <Bank />,
  transfers: <ArrowLeftRight />,
  datarooms: <Folder2Open />,
  'dedicated-datarooms': <FolderCheck />,
  'performance-analysis': <GraphUpArrow />,
  'subscription-agreement-generation': <FileEarmarkText />,
  'video-replays': <CollectionPlay />,
  referential: <Database />,
}

const FALLBACK_ICON = <CircleFill />

const iconFor = (pathname: string): ReactNode => ROUTE_ICONS[pathname] ?? FALLBACK_ICON

type AppSideMenuProps = {
  /** Route config — single source of truth for the navigation entries. */
  routes: Route[]
}

/**
 * Application SideMenu: left-rail navigation derived from the route config.
 *
 * Reuses `getParentRoutes` (permission filtering) for its entries, including
 * sub-navigation: a parent route that has `childOf` children renders as a
 * `SideMenu.Group` with `SubItem`s; a parent without children renders as a flat
 * `SideMenu.Item`. Entries without a label are skipped (guarded detail routes).
 *
 * When the user's only entry is flagged `hideNavigationWhenSoleEntry`, no navigation entry is
 * rendered: the logo and the logout action remain.
 *
 * The Ardian logo and the mobile trigger are rendered automatically by `SideMenu`.
 *
 * Mounted by `Layout` on every page.
 */
export const AppSideMenu: FC<AppSideMenuProps> = ({ routes }) => {
  const { t } = useTranslation()
  const { hasPermission } = useAtomValue(userPermissionsAtom)
  const { logout } = useLogout()
  const { pathname } = useLocation()

  const parents = shouldHideNavigation(routes, hasPermission)
    ? []
    : getNavigationEntries(routes, hasPermission)

  // A route stays active on the pages it owns beyond its exact `to` (e.g. Onboardings anchored at
  // `/` is active on `/onboardings/:id` and `/onboarding/aml/:id`), via `activePathPrefixes`.
  const getClaimLength = (route: Route): number =>
    Math.max(
      -1,
      ...(route.activePathPrefixes ?? [])
        .filter(prefix => pathname.startsWith(prefix))
        .map(prefix => prefix.length),
    )

  // NavLink matches by prefix unless `end` is set. An entry that declares `activePathPrefixes` owns
  // its pages through `active`, so it must only match its exact path natively: otherwise Transfers
  // List (`/transfers`) would stay active on `/transfers/master-funds`.
  const isExactMatch = (route: Route): boolean =>
    route.path === '/' || route.activePathPrefixes !== undefined

  // Among siblings, the most specific prefix wins: a sibling's own path (other than `/`) counts as
  // a prefix too, so List (`/` + `/onboarding`) is not active on `/onboardings/investment-vehicles`.
  const isChildActive = (child: Route, siblings: Route[]): boolean => {
    const claim = getClaimLength(child)
    if (claim < 0) return false

    return siblings.every(sibling => {
      if (sibling === child) return true
      const ownPath = sibling.path && sibling.path !== '/' ? sibling.path : undefined
      const siblingClaim = Math.max(
        getClaimLength(sibling),
        ownPath && pathname.startsWith(ownPath) ? ownPath.length : -1,
      )
      return siblingClaim <= claim
    })
  }

  return (
    <SideMenu>
      {parents.map(parent => {
        const children = getAccessibleChildren(parent, routes, hasPermission).filter(
          route => !!route.label,
        )

        if (children.length === 1 && parent.collapseWhenSoleChild) {
          const [child] = children

          return (
            <SideMenu.Item
              key={parent.pathname}
              to={child.path as string}
              end={isExactMatch(child)}
              active={isChildActive(child, children)}
              icon={iconFor(parent.pathname)}
              label={t(parent.label as string)}
            />
          )
        }

        if (children.length > 0) {
          return (
            <SideMenu.Group
              key={parent.pathname}
              icon={iconFor(parent.pathname)}
              label={t(parent.label as string)}
            >
              {children.map(child => (
                <SideMenu.SubItem
                  key={child.pathname}
                  to={child.path as string}
                  end={isExactMatch(child)}
                  active={isChildActive(child, children)}
                  label={t(child.label as string)}
                />
              ))}
            </SideMenu.Group>
          )
        }

        return (
          <SideMenu.Item
            key={parent.pathname}
            to={parent.path ?? '/'}
            end={parent.path === '/'}
            active={getClaimLength(parent) >= 0}
            icon={iconFor(parent.pathname)}
            label={t(parent.label as string)}
          />
        )
      })}
      <SideMenu.Footer>
        <SideMenu.Action icon={<BoxArrowRight />} label={t('common.logOut')} onClick={logout} />
      </SideMenu.Footer>
    </SideMenu>
  )
}
